import {IBorderRadius} from './common/borderRadius';
import {ShadowType} from './common/elementShadow';
import {IPresentation} from './presentation';

export interface IPresentationTheme {
    _id?: string;
    themeName: string;
    favoriteColors?: string[];
    fontSizes: FontSize[];
    elementStyles?: {
        textElement?: {
            color?: string;
            borderRadius?: IBorderRadius;
            backgroundColor?: string;
            fontSize?: FontSize;
        };
        questionElement?: QuestionElement;
        yesNoQuestionElement?: QuestionElement;
        multiSelectElement?: QuestionElement;
        photoAnswerQuestionElement?: QuestionElement;
        sequenceQuestionElement?: QuestionElement;
        matchPairQuestionElement?: QuestionElement;
        likertScaleQuestionElement?: QuestionElement;
        imageMarkupQuestionElement?: QuestionElement;
        htmlElement?: StaticContentElement;
        imageElement?: ImageTheme;
        buttonElement?: ButtonTheme;
        textInputElement?: FormTitleTheme;
        formElement?: FormTitleTheme;
        shapeElement?: ShapeTheme;
        lineElement?: LineTheme;
    };
    fonts: Font[];
    defaultFonts: Font[];
    allowedUserIds?: string[];
    lastModified: number;
    ownerId?: string;
    presentations?: Pick<IPresentation, '_id' | 'name'>[];
}

export type FontSize = {
    size: FONT_SIZE;
    value: number;
};

export type Font = {
    name: string;
    url: string;
    selected?: boolean;
};

export type QuestionElement = {
    questionFontSize?: FontSize;
    answerFontSize?: FontSize;
    commentFontSize?: FontSize;
    questionBackgroundColor?: string;
    questionBackgroundBorderRadius?: IBorderRadius;
    questionTextAlign?: string;
    fontFamily?: Font;
    correctColor?: string;
    incorrectColor?: string;
    debatableColor?: string;
    correctCommentColor?: string;
    incorrectCommentColor?: string;
    debatableCommentColor?: string;
    selectedColor?: string;
    borderRadius?: IBorderRadius;
    boxShadow?: ShadowType;
};

export type QuestionThemeKey =
    | 'questionElement'
    | 'yesNoQuestionElement'
    | 'multiSelectElement'
    | 'photoAnswerQuestionElement'
    | 'sequenceQuestionElement'
    | 'matchPairQuestionElement'
    | 'likertScaleQuestionElement'
    | 'imageMarkupQuestionElement';

export interface IQuestionElementStyles {
    questionElement?: QuestionElement;
    yesNoQuestionElement?: QuestionElement;
    multiSelectElement?: QuestionElement;
    photoAnswerQuestionElement?: QuestionElement;
    sequenceQuestionElement?: QuestionElement;
    matchPairQuestionElement?: QuestionElement;
    likertScaleQuestionElement?: QuestionElement;
    imageMarkupQuestionElement?: QuestionElement;
}

export type StaticContentElement = {
    color?: string;
    fontSize?: FontSize;
    borderRadius?: IBorderRadius;
    backgroundColor?: string;
};

export type StaticThemeKey = 'textElement' | 'htmlElement' | 'imageElement';

export type ImageTheme = {
    borderRadius?: IBorderRadius;
};

export type ButtonTheme = {
    color?: string;
    fontSize?: FontSize;
    backgroundColor?: string;
};

export type FormTitleTheme = {
    fontSize?: FontSize;
};

export type FormThemeKey = 'buttonElement' | 'textInputElement' | 'formElement';

export type ShapeTheme = {
    color?: string;
    fill?: boolean;
};

export type LineTheme = {
    color?: string;
    thickness?: number;
};

export type ShapeThemeKey = 'shapeElement' | 'lineElement';

export enum FONT_SIZE {
    EXTRA_SMALL = 'EXTRA SMALL',
    SMALL = 'SMALL',
    MEDIUM = 'MEDIUM',
    LARGE = 'LARGE',
    EXTRA_LARGE = 'EXTRA LARGE',
}

export type FontSizeTranslationKey =
    | 'presentationThemesPage.fonts.size.extraSmall'
    | 'presentationThemesPage.fonts.size.small'
    | 'presentationThemesPage.fonts.size.medium'
    | 'presentationThemesPage.fonts.size.large'
    | 'presentationThemesPage.fonts.size.extraLarge';

export const getFontSizeTranslationKey = (fontSize: FONT_SIZE): FontSizeTranslationKey | '' => {
    switch (fontSize) {
        case FONT_SIZE.EXTRA_SMALL:
            return 'presentationThemesPage.fonts.size.extraSmall';
        case FONT_SIZE.SMALL:
            return 'presentationThemesPage.fonts.size.small';
        case FONT_SIZE.MEDIUM:
            return 'presentationThemesPage.fonts.size.medium';
        case FONT_SIZE.LARGE:
            return 'presentationThemesPage.fonts.size.large';
        case FONT_SIZE.EXTRA_LARGE:
            return 'presentationThemesPage.fonts.size.extraLarge';
        default:
            return '';
    }
};
